/* ============================================================================
   PushLifecycle — makes delivered remote pushes actually visible, and keeps a
   device token on file.

   WHY THIS EXISTS
     APNs delivery was proven working (notification_jobs rows show sent_at set,
     no error), yet no push was ever seen. Two client-side gaps:

       1. Foreground pushes were dropped by iOS. Our routinely-fired push (N-06
          collection milestone) is sent the moment a scan crosses a dex
          threshold — i.e. while the app is open. iOS only shows a foreground
          push when presentationOptions is set (now done in capacitor.config.ts)
          AND, for anything beyond the banner, when the app handles the event.

       2. Registration was gesture-only. The APNs token was only POSTed from a
          one-time Settings tap / value-moment prompt, so a rotated token or an
          account that skipped that gesture had nothing on file and every
          sendPush died `no_devices`.

   This headless component (renders null) sits inside AuthProvider so it can read
   the bearer token. It:
     - re-registers the APNs token on launch / sign-in for a permitted native user
     - refreshes the in-app inbox when a push lands in the foreground
     - routes a tapped push to its target route (href in the push payload)

   Everything is best-effort and native-gated inside the notifications helpers;
   on web it is a no-op.
============================================================================ */
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/components/AuthProvider";
import { API_BASE } from "@/lib/apiBase";
import { registerPushOnLaunch, addPushDisplayListeners, NOTIFICATION_PERMISSION_EVENT } from "@/lib/notifications";
import { startLiveActivityListeners } from "@/lib/liveActivities";
import { fetchLiveEventPrefs } from "@/lib/liveEventPrefs";
import { armSubscribedEventsFromFeed } from "@/lib/liveEventArming";
import { normalizeHashHref, hashNavigate } from "@/lib/hashRouter";
import { extractHref } from "@/lib/pushPayload";

export function PushLifecycle(): null {
  const { isSignedIn, token } = useAuth();
  const queryClient = useQueryClient();

  // Launch / sign-in registration: ensure a fresh APNs token is on file for a
  // signed-in native user who has already granted permission. Never prompts.
  useEffect(() => {
    if (!isSignedIn || !token) return;
    void registerPushOnLaunch(API_BASE, token);
    // The event-start Live Activity's tokens (iOS 17.2+): the push-to-start
    // token goes on file so the Worker can wake this phone an hour before a
    // reminded event; a no-op everywhere else.
    void startLiveActivityListeners();
    // The account's live-events choice (lib/liveEventPrefs, 2026-10-30)
    // replaces this device's copy, then this phone arms the upcoming events
    // it covers (Android alarms, notification-only, older iPhones).
    void fetchLiveEventPrefs().then(() => armSubscribedEventsFromFeed(queryClient));
    // And again the moment a permission ask comes back granted (Settings,
    // the broadcast pill), so the token is on file before the next launch.
    const onPermission = (e: Event) => {
      if ((e as CustomEvent<string>).detail === "granted") void registerPushOnLaunch(API_BASE, token);
    };
    window.addEventListener(NOTIFICATION_PERMISSION_EVENT, onPermission);
    return () => window.removeEventListener(NOTIFICATION_PERMISSION_EVENT, onPermission);
  }, [isSignedIn, token]);

  // Foreground + tap listeners (added once; cleaned up on unmount).
  useEffect(() => {
    let cleanup: (() => void) | undefined;
    let cancelled = false;
    void addPushDisplayListeners({
      onForeground: () => {
        // The banner is shown natively via presentationOptions; refresh the
        // inbox so the bell badge reflects any matching in-app row (e.g. a
        // friend-dex-progress push that also wrote a notifications row).
        queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      },
      onTap: (data) => {
        const href = extractHref(data);
        if (!href) return;
        const { external, to } = normalizeHashHref(href);
        if (!external) hashNavigate(to);
      },
    }).then((fn) => {
      // If the component unmounted before the async wiring resolved, remove the
      // listeners immediately instead of leaking them.
      if (cancelled) fn();
      else cleanup = fn;
    });
    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [queryClient]);

  return null;
}
